import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { X, Check, ZoomIn } from "lucide-react";

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

async function getCroppedBlob(src, area) {
  const img = await createImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, 512, 512);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
}

// Simple square crop + zoom before upload.
export default function AvatarCropModal({ imageSrc, open, onCancel, onCropped }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onComplete = useCallback((_, px) => setAreaPixels(px), []);

  const apply = async () => {
    if (!areaPixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(imageSrc, areaPixels);
      onCropped(blob);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onCancel()}>
      <DialogContent className="max-w-md gap-0 overflow-hidden border-white/10 bg-[#0a0a0a] p-0 [&>button]:hidden" data-testid="crop-modal">
        <DialogTitle className="sr-only">Position your photo</DialogTitle>
        <DialogDescription className="sr-only">Crop and zoom your profile photo before uploading it.</DialogDescription>
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div className="font-display text-lg font-black tracking-tight">Position your photo</div>
          <button onClick={() => !busy && onCancel()} className="text-zinc-500 hover:text-white" data-testid="crop-cancel">
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>
        <div className="relative h-72 w-full bg-black">
          {imageSrc && (
            <Cropper
              image={imageSrc} crop={crop} zoom={zoom} aspect={1} cropShape="round" showGrid={false}
              onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete}
            />
          )}
        </div>
        <div className="space-y-4 px-5 py-5">
          <div className="flex items-center gap-3">
            <ZoomIn className="h-4 w-4 text-zinc-500" />
            <input type="range" min={1} max={3} step={0.01} value={zoom} data-testid="crop-zoom"
              onChange={(e) => setZoom(Number(e.target.value))} className="w-full accent-white" />
          </div>
          <button onClick={apply} disabled={busy} data-testid="crop-apply"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-white py-3 font-display text-sm font-black uppercase tracking-wide text-black transition-transform hover:bg-zinc-200 active:scale-95 disabled:opacity-40">
            <Check className="h-4 w-4" /> {busy ? "Processing…" : "Apply & upload"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
